import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Sparkles, ChevronRight, ChevronLeft, Maximize2, Minimize2,
  NotebookPen, LayoutGrid, Target, Cpu, Database, ShieldCheck,
  TrendingUp, Building2, ClipboardCheck, Star, Users, BarChart3,
  Rocket, Brain, BellRing, MessageSquareText, ArrowLeft, ArrowRight, Server,
  Globe, Zap, CheckCircle2, Layers, Palette, Compass, Gift, Lock
} from 'lucide-react';
import { PRESENTATION_SLIDES } from '../data/presentationData';

interface PresentationDashboardProps {
  selectedBrand?: any;
  selectedBranch?: any;
  selectedPeriod?: string;
}

// نگاشت نام آیکون به کامپوننت lucide برای استفاده در داده‌ها
const ICONS: Record<string, React.ComponentType<any>> = {
  Sparkles, LayoutGrid, Target, Cpu, Database, ShieldCheck, TrendingUp,
  Building2, ClipboardCheck, Star, Users, BarChart3, Rocket, Brain,
  BellRing, MessageSquareText, Server, Globe, Zap, CheckCircle2,
  Layers, Palette, Compass, Gift, Lock
};

const Icon: React.FC<{ name: string; className?: string }> = ({ name, className }) => {
  const Cmp = ICONS[name] || Sparkles;
  return <Cmp className={className} />;
};

const TOTAL = PRESENTATION_SLIDES.length;

// انواع ترنزیشن ورود اسلاید (الهام از Reveal / Random Bars / Fall Over / Drape / Curtains / Wind)
// به‌ترتیب روی اسلایدها چرخش می‌کند تا هر اسلاید ورود متفاوت و چشمگیر داشته باشد.
const SLIDE_ANIMS = ['revealIn', 'curtainsIn', 'fallOver', 'barsIn', 'drapeIn', 'windIn'];

const PresentationDashboard: React.FC<PresentationDashboardProps> = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1); // 1=بعدی، -1=قبلی
  // یادداشت ارائه‌دهنده موقتاً برای انتشار همگانی غیرفعال است (showNotes همیشه false)
  const [showNotes] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const goTo = useCallback((next: number, dir: 1 | -1) => {
    setDirection(dir);
    setIndex(() => {
      if (next < 0) return 0;
      if (next > TOTAL - 1) return TOTAL - 1;
      return next;
    });
  }, []);

  const nextSlide = useCallback(() => goTo(index + 1, 1), [index, goTo]);
  const prevSlide = useCallback(() => goTo(index - 1, -1), [index, goTo]);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  // ناوبری با صفحه‌کلید (RTL: فلش چپ = اسلاید بعدی)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
        case 'PageDown':
        case ' ':
          e.preventDefault();
          nextSlide();
          break;
        case 'ArrowRight':
        case 'PageUp':
          e.preventDefault();
          prevSlide();
          break;
        case 'Home':
          goTo(0, -1);
          break;
        case 'End':
          goTo(TOTAL - 1, 1);
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        // case 'n':
        // case 'N':
        //   setShowNotes((s) => !s); // موقتاً برای انتشار همگانی غیرفعال
        //   break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nextSlide, prevSlide, goTo, toggleFullscreen]);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const slide = PRESENTATION_SLIDES[index];
  const progress = ((index + 1) / TOTAL) * 100;

  return (
    <div
      ref={containerRef}
      dir="rtl"
      className="presentation-root relative flex w-full flex-col overflow-hidden rounded-3xl"
      style={{
        background:
          'radial-gradient(1100px 560px at 88% -12%, rgba(0,172,146,0.08), transparent 60%),' +
          'radial-gradient(820px 480px at 6% 112%, rgba(0,193,255,0.07), transparent 55%),' +
          'linear-gradient(165deg, #FFFFFF 0%, #F6FBFB 55%, #EFFAF8 100%)',
        height: isFullscreen ? '100vh' : 'calc(100vh - 128px)',
      }}
    >
      {/* نوار پیشرفت بالا */}
      <div className="absolute top-0 left-0 right-0 z-20 h-1 bg-slate-200/70">
        <div
          className="h-full bg-gradient-to-l from-teal-600 to-cyan-500 transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* نوار ابزار بالا */}
      <div className="absolute top-5 left-5 right-6 z-20 flex items-center justify-between">
        <div className="flex items-center gap-2 rounded-full bg-white/80 px-3 py-1.5 shadow-sm ring-1 ring-slate-200 backdrop-blur">
          <Sparkles className="h-4 w-4 text-teal-600" />
          <span className="text-xs font-bold text-slate-700">نگاه هوشمند</span>
          <span className="text-[10px] tracking-wider text-slate-400">INSIGHT</span>
        </div>
        <div className="flex items-center gap-2">
          {/* دکمه‌ی یادداشت ارائه‌دهنده — موقتاً برای انتشار همگانی غیرفعال شده است
          <button
            onClick={() => setShowNotes((s) => !s)}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium shadow-sm ring-1 transition-all ${
              showNotes
                ? 'bg-teal-600 text-white ring-teal-600'
                : 'bg-white/80 text-slate-600 ring-slate-200 hover:bg-white'
            }`}
            title="یادداشت ارائه‌دهنده (N)"
          >
            <NotebookPen className="h-3.5 w-3.5" />
            یادداشت
          </button>
          */}
          <button
            onClick={toggleFullscreen}
            className="flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm ring-1 ring-slate-200 backdrop-blur transition-all hover:bg-white"
            title="تمام‌صفحه (F)"
          >
            {isFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            {isFullscreen ? 'خروج' : 'تمام‌صفحه'}
          </button>
        </div>
      </div>

      {/* محتوای اسلاید */}
      <div className="relative z-10 flex flex-1 items-center justify-center overflow-y-auto px-6 pt-16 pb-4 md:px-20">
        <div
          key={`${index}-${isFullscreen ? 'fs' : 'win'}`}
          className="slide-stage w-full max-w-6xl"
          style={{ animation: `${direction === 1 ? SLIDE_ANIMS[index % SLIDE_ANIMS.length] : 'drapeIn'} 2.2s cubic-bezier(0.22, 1, 0.36, 1) both` }}
        >
          <SlideView slide={slide} />
        </div>
      </div>

      {/* یادداشت ارائه‌دهنده */}
      {showNotes && slide.notes && (
        <div className="absolute bottom-20 left-1/2 z-30 w-[92%] max-w-3xl -translate-x-1/2 rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-2xl backdrop-blur-xl">
          <div className="mb-2 flex items-center gap-2 text-teal-700">
            <NotebookPen className="h-4 w-4" />
            <span className="text-xs font-bold">چه بگویم در این اسلاید</span>
          </div>
          <ul className="space-y-1.5">
            {slide.notes.map((n: string, i: number) => (
              <li key={i} className="flex items-start gap-2 text-[13px] leading-relaxed text-slate-600">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-500" />
                {n}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ناوبری پایین */}
      <div className="relative z-20 flex shrink-0 items-center justify-between border-t border-slate-200/60 bg-white/40 px-6 py-3.5 backdrop-blur-sm md:px-20">
        <button
          onClick={prevSlide}
          disabled={index === 0}
          className="flex items-center gap-1 rounded-full bg-white px-4 py-2 text-sm text-slate-600 shadow-sm ring-1 ring-slate-200 transition-all hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronRight className="h-4 w-4" />
          قبلی
        </button>

        {/* نقطه‌ها */}
        <div className="flex items-center gap-2">
          {PRESENTATION_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i, i > index ? 1 : -1)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === index ? 'w-7 bg-teal-600' : 'w-2 bg-slate-300 hover:bg-slate-400'
              }`}
              aria-label={`اسلاید ${i + 1}`}
            />
          ))}
          <span className="mr-3 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold tabular-nums text-slate-700 ring-1 ring-slate-200">
            {index + 1} / {TOTAL}
          </span>
        </div>

        <button
          onClick={nextSlide}
          disabled={index === TOTAL - 1}
          className="flex items-center gap-1 rounded-full bg-gradient-to-l from-teal-600 to-cyan-500 px-4 py-2 text-sm font-medium text-white shadow-md shadow-teal-300/50 transition-all hover:shadow-lg hover:shadow-teal-300/60 disabled:cursor-not-allowed disabled:opacity-40"
        >
          بعدی
          <ChevronLeft className="h-4 w-4" />
        </button>
      </div>

      {/* انیمیشن‌های اختصاصی (سبک Prezi: زوم نرم) */}
      <style>{`
        /* Reveal — محو + لغزش نرم + بلور */
        @keyframes revealIn {
          0%   { opacity: 0; transform: translateX(4%) scale(0.985); filter: blur(7px); }
          60%  { filter: blur(0); }
          100% { opacity: 1; transform: translateX(0) scale(1); filter: blur(0); }
        }
        /* Curtains — بازشدن پرده از وسط */
        @keyframes curtainsIn {
          0%   { opacity: 0.35; clip-path: inset(0 49% 0 49%); }
          100% { opacity: 1; clip-path: inset(0 0 0 0); }
        }
        /* Fall Over — افتادن سه‌بعدی از بالا */
        @keyframes fallOver {
          0%   { opacity: 0; transform: perspective(1600px) rotateX(-72deg); transform-origin: top center; }
          100% { opacity: 1; transform: perspective(1600px) rotateX(0deg); transform-origin: top center; }
        }
        /* Random Bars — وایپ عمودی (برداشته‌شدن میله‌ای) */
        @keyframes barsIn {
          0%   { opacity: 0.3; clip-path: inset(0 0 100% 0); }
          100% { opacity: 1; clip-path: inset(0 0 0 0); }
        }
        /* Drape — افتادن پارچه‌ای از بالا */
        @keyframes drapeIn {
          0%   { opacity: 0; transform: translateY(-9%) scaleY(0.72); transform-origin: top center; }
          100% { opacity: 1; transform: translateY(0) scaleY(1); transform-origin: top center; }
        }
        /* Wind — وزش باد (کجی + لغزش + بلور) */
        @keyframes windIn {
          0%   { opacity: 0; transform: translateX(13%) skewX(7deg); filter: blur(10px); }
          60%  { filter: blur(0); }
          100% { opacity: 1; transform: translateX(0) skewX(0deg); filter: blur(0); }
        }
        /* انیمیشن پلکانیِ اجزا حذف شد چون محتوا را نامرئی نگه می‌داشت و افکت ورودِ اسلاید را پنهان می‌کرد */
        @keyframes floatY {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        /* رشد خط تأکید زیر عنوان‌ها */
        @keyframes growBar {
          0%   { transform: scaleX(0); opacity: 0; }
          100% { transform: scaleX(1); opacity: 1; }
        }
        /* تپش نرم برچسب لیمویی «شما ↔ بازار» */
        @keyframes softPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(132, 204, 22, 0.5); }
          50%      { transform: scale(1.06); box-shadow: 0 0 0 6px rgba(132, 204, 22, 0); }
        }
        /* تنفس نور پس‌زمینه‌ی اسلاید نبض بازار */
        @keyframes glowPulse {
          0%, 100% { opacity: 0.45; }
          50%      { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .slide-stage, .slide-stage > * > *, .presentation-root [style*="animation"] { animation: none !important; }
        }
        .presentation-root :focus { outline: none; }
      `}</style>
    </div>
  );
};

/* ---------- نمایش هر اسلاید بر اساس نوع ---------- */

const SlideView: React.FC<{ slide: any }> = ({ slide }) => {
  switch (slide.type) {
    case 'cover':
      return <CoverSlide slide={slide} />;
    case 'intro':
      return <IntroSlide slide={slide} />;
    case 'goal':
      return <GoalSlide slide={slide} />;
    case 'architecture':
      return <ArchitectureSlide slide={slide} />;
    case 'reports':
      return <ReportsSlide slide={slide} />;
    case 'dashboards':
      return <DashboardsSlide slide={slide} />;
    case 'marketpulse':
      return <MarketPulseSlide slide={slide} />;
    case 'screenshot':
      return <ScreenshotSlide slide={slide} />;
    case 'changes':
      return <ChangesSlide slide={slide} />;
    case 'transition':
      return <TransitionSlide slide={slide} />;
    case 'roadmap':
      return <RoadmapSlide slide={slide} />;
    case 'closing':
      return <ClosingSlide slide={slide} />;
    default:
      return null;
  }
};

// رندر متن با پشتیبانی از **بولد** (کلمات مهم با رنگ تأکید برجسته می‌شوند)
const RichText: React.FC<{ text: string }> = ({ text }) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('**') && p.endsWith('**') ? (
          <strong key={i} className="font-extrabold text-teal-700">{p.slice(2, -2)}</strong>
        ) : (
          <React.Fragment key={i}>{p}</React.Fragment>
        )
      )}
    </>
  );
};

// تبدیل عدد لاتین به رقم فارسی (مشترک با کارت‌های نبض بازار)
const toFaNum = (n: number) => String(n).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[+d]);

// شمارنده‌ی انیمیشنی (count-up) با احترام به prefers-reduced-motion
const CountUp: React.FC<{ to: number; duration?: number; className?: string }> = ({ to, duration = 1200, className }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setVal(to); return; }
    let raf = 0;
    let start = 0;
    const tick = (t: number) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return <span className={className}>{toFaNum(val)}</span>;
};

// گیج نیم‌دایره‌ای «حسی ↔ داده‌محور» (value: ۰..۱۰۰، عقربه به‌سمت چپ=داده‌محور)
const Gauge: React.FC<{ value: number; rightLabel: string; leftLabel: string }> = ({ value, rightLabel, leftLabel }) => {
  const v = Math.max(0, Math.min(100, value));
  // زاویه‌ی عقربه: ۱۸۰° (راست) تا ۰° (چپ)
  const angle = 180 - (v / 100) * 180;
  const cx = 100, cy = 100, r = 80;
  const rad = (deg: number) => (deg * Math.PI) / 180;
  const nx = cx + r * 0.82 * Math.cos(rad(angle));
  const ny = cy - r * 0.82 * Math.sin(rad(angle));
  return (
    <div className="flex flex-col items-center" dir="ltr">
      <svg viewBox="0 0 200 120" className="w-44">
        <defs>
          <linearGradient id="gaugeGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#0d9488" />
            <stop offset="100%" stopColor="#fb7185" />
          </linearGradient>
        </defs>
        {/* قوس زمینه */}
        <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke="#e2e8f0" strokeWidth="14" strokeLinecap="round" />
        {/* قوس رنگی */}
        <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke="url(#gaugeGrad)" strokeWidth="14" strokeLinecap="round" />
        {/* عقربه */}
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#0f172a" strokeWidth="4" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="6" fill="#0f172a" />
      </svg>
      <div className="-mt-2 flex w-44 justify-between text-xs font-bold">
        <span className="text-teal-700">{leftLabel}</span>
        <span className="text-rose-500">{rightLabel}</span>
      </div>
    </div>
  );
};

const Kicker: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-teal-50 px-4 py-2 text-sm font-bold text-teal-700 ring-1 ring-teal-100">
    <Sparkles className="h-4 w-4" />
    {children}
  </div>
);

const AccentBar: React.FC = () => (
  <div
    className="mx-auto mt-5 h-1 w-16 origin-right rounded-full bg-gradient-to-l from-teal-600 to-cyan-500"
    style={{ animation: 'growBar 0.7s 0.35s both cubic-bezier(0.22, 1, 0.36, 1)' }}
  />
);

const SectionTitle: React.FC<{ kicker: string; title: string; desc?: string }> = ({ kicker, title, desc }) => (
  <div className="mb-12 text-center">
    <Kicker>{kicker}</Kicker>
    <h2 className="text-4xl max-lg:text-3xl font-extrabold tracking-tight text-slate-900 lg:text-5xl">{title}</h2>
    <AccentBar />
    {desc && <p className="mx-auto mt-5 max-w-3xl text-xl leading-relaxed text-slate-600"><RichText text={desc} /></p>}
  </div>
);

const CoverSlide: React.FC<{ slide: any }> = ({ slide }) => (
  <div className="text-center">
    <div
      className="mx-auto mb-9 flex h-24 w-24 items-center justify-center rounded-3xl bg-gradient-to-br from-teal-600 to-cyan-600 shadow-xl shadow-teal-300/50"
      style={{ animation: 'floatY 4s ease-in-out infinite' }}
    >
      <Sparkles className="h-12 w-12 text-white" />
    </div>
    <h1 className="text-4xl max-lg:text-3xl font-black tracking-tight text-slate-900 lg:text-7xl xl:text-8xl">{slide.title}</h1>
    <p className="mt-3 text-xl font-bold tracking-[0.3em] text-teal-600">{slide.subtitle}</p>
    {slide.version && (
      <div
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-l from-teal-600 to-cyan-500 px-6 py-2.5 text-base font-extrabold text-white shadow-lg shadow-teal-300/50"
        style={{ animation: 'softPulse 2.6s ease-in-out infinite' }}
      >
        <Sparkles className="h-4 w-4" />
        {slide.version}
      </div>
    )}
    <p className="mx-auto mt-8 max-w-2xl text-2xl font-medium leading-relaxed text-slate-600">{slide.tagline}</p>
    {/* <div className="mt-12 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm text-slate-500 shadow-sm ring-1 ring-slate-200">
      <ChevronLeft className="h-4 w-4 animate-pulse text-teal-600" />
      برای شروع، فلش چپ را بزنید
    </div> */}
  </div>
);

const IntroSlide: React.FC<{ slide: any }> = ({ slide }) => (
  <div>
    <SectionTitle kicker={slide.kicker} title={slide.title} desc={slide.desc} />
    {slide.points && (
      <div className="mx-auto mt-2 max-w-3xl space-y-3">
        {slide.points.map((p: string, i: number) => (
          <div
            key={i}
            className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-sm transition-all hover:border-teal-200 hover:shadow-md"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
              <CheckCircle2 className="h-6 w-6" />
            </span>
            <span className="text-lg font-medium text-slate-700"><RichText text={p} /></span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const GoalSlide: React.FC<{ slide: any }> = ({ slide }) => (
  <div>
    <SectionTitle kicker={slide.kicker} title={slide.title} />
    <div className="grid items-stretch gap-6 md:grid-cols-[1fr_auto_1fr]">
      <div className="rounded-2xl border border-rose-100 bg-rose-50/50 p-7">
        <h3 className="mb-5 text-xl font-extrabold text-rose-600">{slide.problem.title}</h3>
        <ul className="space-y-4">
          {slide.problem.items.map((it: string, i: number) => (
            <li key={i} className="flex items-start gap-3 text-lg font-medium text-slate-600">
              <span className="mt-2.5 h-2 w-2 shrink-0 rounded-full bg-rose-400" />
              <span><RichText text={it} /></span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col items-center justify-center gap-2 px-2">
        <Gauge value={slide.gaugeValue ?? 80} rightLabel="حسی" leftLabel="داده‌محور" />
        <ArrowRight className="h-5 w-5 text-teal-500" />
      </div>

      <div className="rounded-2xl border border-teal-200 bg-teal-50/60 p-7">
        <h3 className="mb-5 text-xl font-extrabold text-teal-700">{slide.goal.title}</h3>
        <ul className="space-y-4">
          {slide.goal.items.map((it: string, i: number) => (
            <li key={i} className="flex items-start gap-3 text-lg font-medium text-slate-700">
              <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-teal-600" />
              <span><RichText text={it} /></span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const ArchitectureSlide: React.FC<{ slide: any }> = ({ slide }) => (
  <div>
    <SectionTitle kicker={slide.kicker} title={slide.title} desc={slide.desc} />
    <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-center md:justify-between">
      {slide.flow.map((step: any, i: number) => (
        <React.Fragment key={i}>
          <div className="flex-1 rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
              <Icon name={step.icon} className="h-6 w-6" />
            </div>
            <div className="text-lg font-bold text-slate-800">{step.title}</div>
            <div className="mt-1 text-sm leading-relaxed text-slate-400">{step.sub}</div>
          </div>
          {i < slide.flow.length - 1 && (
            <ChevronLeft className="mx-auto hidden h-6 w-6 shrink-0 text-teal-400 md:block" />
          )}
        </React.Fragment>
      ))}
    </div>
    {slide.badges && (
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        {slide.badges.map((b: any, i: number) => (
          <div
            key={i}
            className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm"
          >
            <Icon name={b.icon} className="h-4 w-4 text-teal-600" />
            {b.label}
          </div>
        ))}
      </div>
    )}
  </div>
);

const ReportsSlide: React.FC<{ slide: any }> = ({ slide }) => (
  <div>
    <SectionTitle kicker={slide.kicker} title={slide.title} desc={slide.desc} />
    <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
      {slide.reports.map((r: any, i: number) => (
        <div
          key={i}
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:border-teal-200 hover:shadow-md"
        >
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
            <Icon name={r.icon} className="h-6 w-6" />
          </div>
          <h3 className="text-xl font-bold text-slate-800">{r.title}</h3>
          <p className="mt-1.5 text-base leading-relaxed text-slate-500">{r.desc}</p>
        </div>
      ))}
    </div>
    <p className="mt-6 text-center text-base font-medium text-slate-400">… و ده‌ها گزارش دیگر</p>
  </div>
);

const DashboardsSlide: React.FC<{ slide: any }> = ({ slide }) => (
  <div>
    <SectionTitle kicker={slide.kicker} title={slide.title} desc={slide.desc} />
    <div className="grid gap-4 md:grid-cols-3">
      {slide.categories.map((cat: any, i: number) => (
        <div
          key={i}
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:border-teal-200 hover:shadow-md"
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
              <Icon name={cat.icon} className="h-5 w-5" />
            </div>
            <span className="rounded-full bg-teal-50 px-3 py-1 text-sm font-bold text-teal-700">
              {cat.count} داشبورد
            </span>
          </div>
          <h3 className="text-xl font-bold text-slate-800">{cat.title}</h3>
          <p className="mt-1.5 text-base leading-relaxed text-slate-500">{cat.items}</p>
        </div>
      ))}
    </div>
  </div>
);

// تبدیل عدد لاتین به رقم فارسی برای نمایش درصدها
const toFa = (n: number) => String(n).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[+d]);

// یک کارت متریک نبض بازار با نمودار میله‌ای دوتایی «شما / بازار»
const MarketMetricCard: React.FC<{ metric: any; max: number }> = ({ metric, max }) => {
  const gap = metric.you - metric.market;
  const ahead = gap >= 0;
  const youH = Math.max(8, Math.round((metric.you / max) * 100));
  const marketH = Math.max(8, Math.round((metric.market / max) * 100));
  return (
    <div className="flex flex-col rounded-2xl border border-teal-200 bg-white p-5 text-center shadow-md shadow-teal-100">
      <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-teal-100 text-teal-700">
        <Icon name={metric.icon} className="h-5 w-5" />
      </div>
      <h3 className="text-lg font-bold text-slate-800">{metric.title}</h3>

      {/* نمودار میله‌ای دوتایی */}
      <div className="mt-4 flex h-28 items-end justify-center gap-3" dir="ltr">
        <div className="flex w-12 flex-col items-center justify-end">
          <span className="mb-1 text-base font-extrabold text-teal-700">+{toFa(metric.you)}٪</span>
          <div className="w-full rounded-t-md bg-gradient-to-t from-teal-600 to-cyan-500" style={{ height: `${youH}%` }} />
        </div>
        <div className="flex w-12 flex-col items-center justify-end">
          <span className="mb-1 text-base font-extrabold text-slate-400">+{toFa(metric.market)}٪</span>
          <div className="w-full rounded-t-md bg-slate-300" style={{ height: `${marketH}%` }} />
        </div>
      </div>
      <div className="mt-2 flex justify-center gap-4 text-sm font-bold" dir="ltr">
        <span className="text-teal-700">شما</span>
        <span className="text-slate-400">بازار</span>
      </div>

      {/* فاصله تا بازار */}
      <span
        className={`mt-3 inline-block rounded-full px-3 py-1 text-sm font-bold ${
          ahead ? 'bg-lime-300 text-teal-900' : 'bg-rose-100 text-rose-600'
        }`}
      >
        {ahead ? `${toFa(gap)}٪ جلوتر` : `${toFa(Math.abs(gap))}٪ عقب‌تر`}
      </span>
    </div>
  );
};

const MarketPulseSlide: React.FC<{ slide: any }> = ({ slide }) => {
  const max = Math.max(...slide.metrics.flatMap((m: any) => [m.you, m.market]));
  return (
    <div className="relative">
      {/* درخشش ویژه‌ی پس‌زمینه */}
      <div
        className="pointer-events-none absolute -inset-x-16 -top-16 h-56 bg-teal-300/20 blur-[100px]"
        style={{ animation: 'glowPulse 4s ease-in-out infinite' }}
      />

      <div className="relative mb-10 text-center">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-l from-teal-600 to-cyan-500 px-5 py-2 text-xs font-bold text-white shadow-md shadow-teal-300/50">
          <Sparkles className="h-4 w-4" />
          {slide.badge}
        </div>
        <h2 className="text-3xl max-lg:text-2xl font-black leading-tight tracking-tight text-slate-900 lg:text-6xl">{slide.title}</h2>
        <p className="mt-5 text-xl font-extrabold text-teal-700">{slide.subtitle}</p>
        <p className="mx-auto mt-5 max-w-2xl text-lg leading-loose text-slate-600"><RichText text={slide.desc} /></p>
      </div>

      {/* راهنمای واحدِ «شما ↔ بازار» برای کل بخش */}
      <div className="relative mb-5 flex items-center justify-center gap-3">
        <span
          className="inline-flex items-center gap-2 rounded-full bg-lime-300 px-4 py-1.5 text-xs font-bold text-teal-900"
          style={{ animation: 'softPulse 2.4s ease-in-out infinite' }}
        >
          شما ↔ بازار
        </span>
        {slide.sampleNote && (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-400 ring-1 ring-slate-200">
            {slide.sampleNote}
          </span>
        )}
      </div>

      <div className="relative grid gap-5 sm:grid-cols-2 md:grid-cols-4">
        {slide.metrics.map((m: any, i: number) => (
          <MarketMetricCard key={i} metric={m} max={max} />
        ))}
      </div>

      <div className="relative mt-8 flex items-center justify-center gap-2.5 rounded-2xl border border-teal-200 bg-teal-50/70 px-5 py-5 text-center">
        <Sparkles className="h-5 w-5 shrink-0 text-teal-600" />
        <span className="text-lg font-bold text-teal-700">{slide.highlight}</span>
      </div>
    </div>
  );
};

// نمایش اسکرین‌شات واقعی داشبورد داخل یک قاب مرورگر
const ScreenshotSlide: React.FC<{ slide: any }> = ({ slide }) => (
  <div>
    <SectionTitle kicker={slide.kicker} title={slide.title} desc={slide.desc} />
    <div className="mx-auto max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-300/40 ring-1 ring-slate-900/5">
      {/* نوار بالای قاب مرورگر */}
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
        <span className="h-3 w-3 rounded-full bg-rose-400" />
        <span className="h-3 w-3 rounded-full bg-amber-400" />
        <span className="h-3 w-3 rounded-full bg-emerald-400" />
        <span className="mr-3 inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1 text-xs font-medium text-slate-400 ring-1 ring-slate-200">
          <Lock className="h-3 w-3" />
          insight · نبض بازار
        </span>
      </div>
      <img
        src={encodeURI(slide.image)}
        alt={slide.title}
        loading="eager"
        className="block w-full"
      />
    </div>
    {slide.caption && (
      <p className="mx-auto mt-5 max-w-3xl text-center text-base leading-relaxed text-slate-500">
        <RichText text={slide.caption} />
      </p>
    )}
  </div>
);

const ChangesSlide: React.FC<{ slide: any }> = ({ slide }) => (
  <div>
    <SectionTitle kicker={slide.kicker} title={slide.title} desc={slide.desc} />

    {/* شمارنده‌ی رشد تعداد داشبوردها */}
    {slide.stat && (
      <div className="mx-auto mb-8 flex max-w-3xl items-center justify-center gap-6 rounded-2xl border border-teal-200 bg-white px-6 py-5 shadow-sm">
        <div className="text-center">
          <div className="text-4xl font-black text-slate-300">{toFaNum(slide.stat.beforeCount)}</div>
          <div className="mt-1 text-sm font-medium text-slate-400">نسخه قبل</div>
        </div>
        {/* نوار رشد */}
        <div className="relative h-3 flex-1 overflow-hidden rounded-full bg-slate-100" dir="ltr">
          <div
            className="absolute inset-y-0 right-0 rounded-full bg-gradient-to-l from-teal-600 to-cyan-500"
            style={{ width: '100%', animation: 'growBar 1.2s 0.2s both cubic-bezier(0.22,1,0.36,1)', transformOrigin: 'right' }}
          />
        </div>
        <div className="text-center">
          <div className="text-3xl max-lg:text-2xl font-black text-teal-600 lg:text-5xl">
            <CountUp to={slide.stat.afterCount} />{slide.stat.suffix ?? ''}
          </div>
          <div className="mt-1 text-sm font-bold text-teal-700">{slide.stat.unit}</div>
        </div>
      </div>
    )}

    <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
      <div className="grid grid-cols-[1fr_auto_1.2fr] bg-slate-50 text-base font-bold text-slate-400">
        <div className="p-3.5 text-center">نسخه قبل</div>
        <div className="p-3.5" />
        <div className="p-3.5 text-center text-teal-700">نسخه جدید</div>
      </div>
      {slide.rows.map((r: any, i: number) => (
        <div
          key={i}
          className={`grid grid-cols-[1fr_auto_1.2fr] items-center border-t border-slate-100 ${i % 2 ? 'bg-slate-50/40' : 'bg-white'}`}
        >
          <div className="p-4 text-center text-lg text-slate-400">{r.before}</div>
          <div className="px-2">
            <ArrowLeft className="h-5 w-5 text-teal-500" />
          </div>
          <div className="flex items-center gap-2 p-4 text-lg font-medium text-slate-800">
            <Icon name={r.icon} className="h-5 w-5 shrink-0 text-teal-600" />
            <span><RichText text={r.after} /></span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const TransitionSlide: React.FC<{ slide: any }> = ({ slide }) => (
  <div>
    <SectionTitle kicker={slide.kicker} title={slide.title} />
    <div className="grid items-stretch gap-6 md:grid-cols-[1fr_auto_1fr]">
      {/* امروز */}
      <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
        <span className="mb-3 inline-block rounded-full bg-slate-100 px-3 py-1 text-base font-bold text-slate-500">
          {slide.today.label}
        </span>
        <h3 className="mb-1.5 text-2xl font-extrabold text-slate-800">{slide.today.title}</h3>
        <p className="mb-4 text-lg leading-relaxed text-slate-500">{slide.today.desc}</p>
        <div className="flex flex-wrap gap-2">
          {slide.today.items.map((it: string, i: number) => (
            <span key={i} className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-base font-medium text-slate-600">
              <Users className="h-4 w-4 text-slate-400" />
              {it}
            </span>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-teal-100 text-teal-600 shadow-sm">
          <ArrowLeft className="h-6 w-6" />
        </div>
      </div>

      {/* فردا */}
      <div className="rounded-2xl border border-teal-200 bg-teal-50/60 p-7">
        <span className="mb-3 inline-block rounded-full bg-teal-600 px-3 py-1 text-base font-bold text-white">
          {slide.future.label}
        </span>
        <h3 className="mb-1.5 text-2xl font-extrabold text-slate-900">{slide.future.title}</h3>
        <p className="mb-4 text-lg leading-relaxed text-slate-600">{slide.future.desc}</p>
        <div className="flex flex-wrap gap-2">
          {slide.future.items.map((it: string, i: number) => (
            <span key={i} className="flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-base font-medium text-teal-700 ring-1 ring-teal-100">
              <CheckCircle2 className="h-4 w-4 text-teal-600" />
              {it}
            </span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const RoadmapSlide: React.FC<{ slide: any }> = ({ slide }) => (
  <div>
    <SectionTitle kicker={slide.kicker} title={slide.title} desc={slide.desc} />
    <div className="relative mx-auto max-w-5xl">
      {/* خط زمانی افقی پیوند (فقط دسکتاپ) */}
      <div
        className="absolute right-[16%] left-[16%] top-7 hidden h-1 rounded-full bg-gradient-to-l from-teal-500 to-cyan-400 md:block"
        style={{ animation: 'growBar 0.9s 0.3s both cubic-bezier(0.22,1,0.36,1)', transformOrigin: 'right' }}
      />
      <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
        {slide.steps.map((s: any, i: number) => (
          <div key={i} className="relative flex flex-1 flex-col items-center text-center md:px-3">
            {/* نود شماره‌دار روی خط زمانی */}
            <div className="relative z-10 mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-teal-600 to-cyan-500 text-2xl font-black text-white shadow-lg shadow-teal-300/50 ring-4 ring-white">
              {toFaNum(i + 1)}
            </div>
            <div className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <span className="text-base font-bold text-teal-600">{s.phase}</span>
              <div className="mx-auto my-3 flex h-12 w-12 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
                <Icon name={s.icon} className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-800">{s.title}</h3>
              <p className="mt-1.5 text-base leading-relaxed text-slate-500">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ClosingSlide: React.FC<{ slide: any }> = ({ slide }) => (
  <div className="text-center">
    <div
      className="mx-auto mb-9 flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-teal-600 to-cyan-600 shadow-xl shadow-teal-300/50"
      style={{ animation: 'floatY 4s ease-in-out infinite' }}
    >
      <Rocket className="h-10 w-10 text-white" />
    </div>
    <h2 className="text-3xl max-lg:text-2xl font-black tracking-tight text-slate-900 lg:text-5xl">{slide.title}</h2>
    <AccentBar />
    <p className="mx-auto mt-6 max-w-2xl text-xl leading-relaxed text-slate-600"><RichText text={slide.desc} /></p>
    <div className="mx-auto mt-8 flex max-w-3xl flex-wrap justify-center gap-3">
      {slide.takeaways.map((t: string, i: number) => (
        <div
          key={i}
          className="flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-4 py-2 text-base font-medium text-teal-700"
        >
          <CheckCircle2 className="h-5 w-5 text-teal-600" />
          {t}
        </div>
      ))}
    </div>
    <p className="mt-10 text-3xl font-extrabold text-slate-900">{slide.cta}</p>
  </div>
);

export default PresentationDashboard;
